/**
 * Typisierter fetch()-Wrapper. /api wird in Produktion von nginx auf den
 * Hono-Container geproxied (siehe docker/nginx.conf); im Dev-Modus uebernimmt
 * vite.config.ts den Proxy.
 */

const TOKEN_KEY = "finanz.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  auth?: boolean;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (opts.auth !== false) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`/api${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (!response.ok) {
    let code = "UNKNOWN";
    let message = response.statusText;
    try {
      const body = (await response.json()) as { error?: string; message?: string };
      if (body.error) code = body.error;
      if (body.message) message = body.message;
    } catch {
      // Body nicht parsbar — wir behalten Default-Codes.
    }
    throw new ApiError(response.status, code, message);
  }

  // 204 No Content — DELETE endpoints return void; the cast is intentional.
  // Callers must type these as api<void>() to avoid masking missing body.
  // NOTE: `content-length === "0"` is a defensive fallback for 200-with-empty-body;
  // HTTP/2 / chunked responses may omit the header, so this branch only covers
  // proxies that explicitly set it.
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }

  return (await response.json()) as T;
}
